import React, {
  createContext,
  Dispatch,
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import 'react-loading-skeleton/dist/skeleton.css';
import { useGetUncategorizedData } from '../../hooks/query/useGetUncategorizedTransactionsDataForScheduleCQuery';
import { useStore } from '../../store';
import { CategorizeActions } from './components/CategorizeTopSection';
import {
  ACTIONS,
  BUSINESS_PERCENT,
  CATEGORIZE_LIFE,
  ACTION_TYPES,
  STYLES,
  CATEGORIZATION_TYPES,
  COMPONENT_MODES,
  DEFAULT_TAX_ID,
  DEFAULT_INCOME_TAX_ID,
} from './constants';
import { CategorizeMainSection } from './components/CategorizeMainSection';
import Heading from '../../generic-components/Heading';
import { getFilterRangeQueryV2 } from './services';
import { useCategorizeTransactionMutation } from '../../hooks/mutations/useCategorizeTransactionMutation';
import { ICategory, ITransaction, IUncategorizedTransactions } from '../../types';
import { SwitchNav } from '../../generic-components/SwitchNav';
import { useTransactionsCategoriesQuery } from '../../hooks/query/useTransactionsCategoriesQuery';
import { ZeroState } from './components/ZeroState';
import { CATEGORIZATION_SWITCH_ITEMS } from '../dashboard/constants';
import { PreLoading } from './components/PreLoadingState';
import { validateFeatureFlag } from '../../services/featureFlagService';
import { FEATURE_FLAG } from '../../constants/general';
import { trackMixPanel } from '../../services/mixpanel.service';
import { CategorizationPopup } from '../dashboard/components/CategorizationPopup';
import {
  isCategorizedMostlyAsBusiness,
  isCategorizedMostlyAsPersonal,
  isCategorizedOnlyAsBusiness,
  isCategorizedOnlyAsPersonal,
} from '../transactions/utils/categorization';

interface INavItem {
  id: string;
  title: string;
  titleClassName: string;
  titleClassNameSelected:string;
  wrapperClassName: string;
  wrapperClassNameSelected: string;
  isDisabled?: boolean;
}

interface IProps {
  containerClassName: string;
  filterYearValue?: number;
  handleFinishCategorize?: (isManualCategorized: boolean)=> void;
  containerHeader? :string;
  isInitialLoading?: boolean;
  toggleData?: {incomeTrxCnt: number, expenseTrxCnt: number};
  navSwitchClassName?: string;
  onMutateFinish?: () => void;
  onActiveTrxChange?: (data: ITransaction) => void;
  customMode? :string;
  shouldHideHeader?: boolean;
  filterRange?: {startDate: string, endDate: string};
  isZeroState?:boolean;
  leftToCategorize?: (amount: number) => void;
  categorizationProgress?: Dispatch<React.SetStateAction<any>>;
  finishProcessCtaTitle?: string;
  actionTypesToHide?: string[];
  isReadOnlyMode?: boolean;
}

export interface ITriggerData {
  isActive: boolean;
  type: string;
  businessPercent?: number | null;
}

export interface IPayloadData {
  bussinessPercentValue: number | null;
  selectedCategoryId: string | null;
}

const CUSTOM_TYPES_TO_MODE_MAP = {
  SC: COMPONENT_MODES.LIMITED_NON_SWITCH,
  EXPENSE_REPORT: COMPONENT_MODES.LIMITED_NON_SWITCH,
};

export const CategorizeContext = createContext<{isReadOnlyMode: boolean | undefined}>({ isReadOnlyMode: false });

export const CategorizeContainer = ({
  containerClassName, filterYearValue, handleFinishCategorize, containerHeader, isInitialLoading, toggleData, navSwitchClassName, onMutateFinish, customMode, onActiveTrxChange, filterRange, shouldHideHeader, isZeroState, leftToCategorize, categorizationProgress, finishProcessCtaTitle, actionTypesToHide, isReadOnlyMode,
}: IProps) => {
  const { t } = useTranslation();
  const customerData = useStore(state => state.user);
  const triggeredActionType = useStore(state => state.triggeredActionType);
  const setTriggeredActionType = useStore(state => state.setTriggeredActionType);
  const { customerId } = customerData;
  const { WORK, LIFE } = BUSINESS_PERCENT;
  const [componentMode, setComponentMode] = useState<number>();
  const [queryString, setQueryString] = useState<any>('');
  const [triggerData, setTriggerData] = useState<ITriggerData>({ isActive: false, type: '' });
  const [middlewareTriggerData, setMiddlewareTriggerData] = useState<ITriggerData>({ isActive: false, type: '' });
  const [mutableTransactions, setMutableTransactions] = useState<ITransaction[] | null>(null);
  const [isManualCategorized, setIsManualCategorized] = useState<boolean>(false);
  const [isCategoryPicker, setIsCategoryPicker] = useState<boolean>(false);
  const [isMutableTransactionsSet, setIsMutableTransactionsSet] = useState<boolean>(false);
  const [categorizeCount, setCategorizeCount] = useState<number>(0);
  const [categorizationType, setCategorizationType] = useState<string>('');
  const [localTransactionsCounter, setLocalTransactionsCounter] = useState<{incomeTrxCnt: number, expenseTrxCnt: number}>(toggleData ?? { incomeTrxCnt: 0, expenseTrxCnt: 0 });
  const { data: transactionsCategories, refetch: getTransactionsCategories } = useTransactionsCategoriesQuery({ customerId });
  const {
    data: uncategorizedData, refetch: fetchUncategorizedData, isRefetching,
  } = useGetUncategorizedData({
    customerId,
    q: queryString,
    onSuccess: (data: IUncategorizedTransactions) => {
      setCategorizeCount(0);
      const isLeftToCategorizeInDataBase = !!data?.uncategorizedTotalNumOfRecords;
      if (!isLeftToCategorizeInDataBase) {
        if (categorizationType === CATEGORIZATION_TYPES.EXPENSE && validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME) && !!localTransactionsCounter.incomeTrxCnt) {
          if (componentMode !== COMPONENT_MODES.LIMITED_NON_SWITCH) {
            setCategorizationType(CATEGORIZATION_TYPES.INCOME);
          }
        } else if (categorizationType === CATEGORIZATION_TYPES.INCOME && !!localTransactionsCounter.expenseTrxCnt) {
          setCategorizationType(CATEGORIZATION_TYPES.EXPENSE);
        }
      }
      if (handleFinishCategorize && componentMode === COMPONENT_MODES.LIMITED_NON_SWITCH && !localTransactionsCounter.expenseTrxCnt) {
        handleFinishCategorize(isManualCategorized);
        setMutableTransactions([]);
      }
      if (handleFinishCategorize && !localTransactionsCounter.expenseTrxCnt && !localTransactionsCounter.incomeTrxCnt) {
        handleFinishCategorize(isManualCategorized);
        setMutableTransactions([]);
      }
      if (data?.transactionList && !data.transactionList.length && !localTransactionsCounter.expenseTrxCnt && !localTransactionsCounter.incomeTrxCnt && isMutableTransactionsSet) {
        setMutableTransactions([]);
      } else if (data?.transactionList && data?.transactionList?.length) {
        setMutableTransactions([...data.transactionList]);
      }
    },
  });

  const { mutateAsync: mutateCategorizeTransaction } = useCategorizeTransactionMutation();

  const filteredCategories = useMemo(() => {
    if (!transactionsCategories) return [];
    return transactionsCategories?.categories.filter((c: ICategory) => c.transactionType === categorizationType);
  }, [transactionsCategories, categorizationType]);

  const dynamicActions = useMemo(() => {
    if (categorizationType === CATEGORIZATION_TYPES.EXPENSE) {
      if (actionTypesToHide?.length) {
        return ACTIONS.EXPENSES.filter(action => !actionTypesToHide?.includes(action.actionType));
      }
      return ACTIONS.EXPENSES;
    }
    if (actionTypesToHide?.length) {
      return ACTIONS.INCOME.filter(action => !actionTypesToHide?.includes(action.actionType));
    }
    return ACTIONS.INCOME;
  }, [categorizationType]);

  const expensesLeft = useMemo(() => {
    if (uncategorizedData && uncategorizedData?.uncategorizedTotalNumOfRecords) {
      return uncategorizedData.uncategorizedTotalNumOfRecords - categorizeCount;
    }
    return 0;
  }, [uncategorizedData?.uncategorizedTotalNumOfRecords, categorizeCount]);

  const navSwitchData = useMemo(
    () => {
      const { expenseTrxCnt, incomeTrxCnt } = localTransactionsCounter;
      const items: INavItem[] = [...CATEGORIZATION_SWITCH_ITEMS];
      items[0].title = expenseTrxCnt ? `${t('CATEGORIZE_EXPENSES')} (${expenseTrxCnt})` : t('CATEGORIZE_EXPENSES');
      items[0].isDisabled = !expenseTrxCnt;
      items[1].title = incomeTrxCnt ? `${t('CATEGORIZE_INCOME')} (${incomeTrxCnt})` : t('CATEGORIZE_INCOME');
      items[1].isDisabled = !incomeTrxCnt;
      return items;
    },
    [localTransactionsCounter],
  );

  const isFooterShown = useMemo(() => componentMode !== COMPONENT_MODES.UNLIMITED && mutableTransactions && mutableTransactions.length > 0, [componentMode, mutableTransactions]);

  const handleAction = (type: string) => {
    const TYPES_TO_SKIP_CATEGORY = [ACTION_TYPES.LIFE, ACTION_TYPES.NOT_NOW];
    if (type && !isManualCategorized) setIsManualCategorized(true);
    if (!TYPES_TO_SKIP_CATEGORY.includes(type) && (!mutableTransactions?.[0]?.categoryDetails?.categoryExternalId || type === ACTION_TYPES.SPLIT)) {
      setMiddlewareTriggerData({ isActive: true, type });
      setIsCategoryPicker(true);
      return;
    }
    setTriggerData({ isActive: true, type });
  };

  const handleCategoryPick = (categoryId: string | null, businessPercent?: number) => {
    if (!categoryId) {
      setIsCategoryPicker(false);
      return;
    }
    setMutableTransactions(prevState => {
      if (!prevState) return null;
      const localClone = [...prevState];
      localClone[0].categoryDetails.categoryExternalId = categoryId;
      if (businessPercent || businessPercent === 0) localClone[0].categoryDetails.businessPercent = businessPercent;
      return localClone;
    });

    setIsCategoryPicker(false);
    if (middlewareTriggerData.isActive) {
      if (businessPercent === 0) {
        setTriggerData({ ...middlewareTriggerData, type: ACTION_TYPES.LIFE });
      } else {
        setTriggerData(middlewareTriggerData);
      }
      setTimeout(() => {
        setMiddlewareTriggerData({ isActive: false, type: '' });
      }, 1000);
    } else if (businessPercent || businessPercent === 0) {
      setTriggerData({ isActive: true, type: businessPercent === 0 ? ACTION_TYPES.LIFE : ACTION_TYPES.SPLIT });
    }
  };

  const handleSwitch = (switchId: string) => {
    if (isRefetching) return;
    setCategorizationType(switchId);
  };

  const getCategoryIdByType = (type: string) => {
    if (type === ACTION_TYPES.LIFE) {
      return null;
    }
    if (mutableTransactions?.[0]?.categoryDetails?.categoryExternalId) {
      return mutableTransactions[0].categoryDetails.categoryExternalId;
    }
    if (type === ACTION_TYPES.APPROVE) {
      return DEFAULT_INCOME_TAX_ID;
    }
    return DEFAULT_TAX_ID;
  };
  const handleActionFinish = async (triggerType?: string) => {
    if (mutableTransactions) {
      if (triggerType !== ACTION_TYPES.NOT_NOW) {
        try {
          if (mutableTransactions[0]) {
            const suggestedCategory = mutableTransactions[0]?.suggestedTaxCategoryId && transactionsCategories?.categories.find((category: any) => category.id === mutableTransactions[0].suggestedTaxCategoryId);
            const selectedCategory = transactionsCategories?.categories.find((category: any) => category.id === mutableTransactions[0].categoryDetails.categoryExternalId);
            const categorizationTypeString = mutableTransactions[0].categorizationType;
            let categorizationTypeFirstCapital = '';
            if (categorizationTypeString) {
              categorizationTypeFirstCapital = categorizationTypeString.charAt(0).toUpperCase() + categorizationTypeString.slice(1).toLowerCase();
            }
            trackMixPanel('Transaction Categorized', {
              categorizationType: categorizationTypeFirstCapital,
              hasSuggestedTaxCategory: mutableTransactions[0].suggestedTaxCategoryId != null,
              identifier: mutableTransactions[0].transactionId,
              life: triggerData.type === ACTION_TYPES.LIFE,
              matchSuggestedTaxCategory: mutableTransactions[0].suggestedTaxCategoryId === mutableTransactions[0].categoryDetails.categoryExternalId,
              mostly_life: isCategorizedMostlyAsPersonal(mutableTransactions[0].categoryDetails) && !isCategorizedOnlyAsPersonal(mutableTransactions[0].categoryDetails),
              mostly_work: isCategorizedMostlyAsBusiness(mutableTransactions[0].categoryDetails) && !isCategorizedOnlyAsBusiness(mutableTransactions[0].categoryDetails),
              Origin: window.location.pathname?.replace('/', '') || 'Dashboard',
              selectedFromList: true,
              splitted: triggerData.type === ACTION_TYPES.SPLIT,
              wasChanged: false,
              work: triggerData.type === ACTION_TYPES.WORK,
              'Suggested category': suggestedCategory?.title,
              'Selected category': selectedCategory?.title,
            });
          }
          const chosenBusinessPercent = mutableTransactions[0]?.categoryDetails?.businessPercent;
          await mutateCategorizeTransaction({
            customerId,
            transactionId: mutableTransactions[0].transactionId,
            categoryDetails: {
              categoryId:
                getCategoryIdByType(triggerData.type),
              businessPercent: chosenBusinessPercent || (triggerData.type === CATEGORIZE_LIFE ? LIFE : WORK),
            },
          });
        } catch (e) {
          // @TODO show an error messsage ?
          setTriggerData({ isActive: false, type: '' });
          const updatedTrxList = mutableTransactions.splice(1);
          if (updatedTrxList.length) {
            setMutableTransactions(updatedTrxList);
          } else {
            fetchUncategorizedData();
            setTimeout(() => {
              fetchUncategorizedData();
            }, 500);
          }
          return;
        }

        setCategorizeCount((prevState: number) => {
          const updatedCount = prevState + 1;
          return updatedCount;
        });

        // Update Switcher Counter
        if (categorizationType === CATEGORIZATION_TYPES.EXPENSE) {
          setLocalTransactionsCounter(prevState => ({
            ...prevState,
            expenseTrxCnt: localTransactionsCounter.expenseTrxCnt - 1,
          }));
        } else if (categorizationType === CATEGORIZATION_TYPES.INCOME) {
          setLocalTransactionsCounter(prevState => ({
            ...prevState,
            incomeTrxCnt: localTransactionsCounter.incomeTrxCnt - 1,
          }));
        }
      }

      if (triggerType === ACTION_TYPES.WORK || triggerType === ACTION_TYPES.APPROVE) {
        if (onMutateFinish) onMutateFinish();
      }

      setTriggerData({ isActive: false, type: '' });

      const updatedTrxList = mutableTransactions.splice(1);

      if (updatedTrxList.length) {
        setMutableTransactions(updatedTrxList);
      } else {
        fetchUncategorizedData();
        setTimeout(() => {
          fetchUncategorizedData();
        }, 500);
      }
    }
  };
  const handleOnChooseCategoryClick = () => {
    setMiddlewareTriggerData({ isActive: false, type: ACTION_TYPES.CHOOSE_CATEGORY });
    setIsCategoryPicker(true);
  };

  const checkComponentMode = () => {
    const isUnLimited = validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME);
    if (isZeroState) {
      setComponentMode(COMPONENT_MODES.ZERO_STATE);
      return;
    }
    if (customMode) {
      const mode = CUSTOM_TYPES_TO_MODE_MAP[customMode as keyof typeof CUSTOM_TYPES_TO_MODE_MAP];
      setComponentMode(mode);
      return;
    }
    if (isUnLimited) {
      setComponentMode(COMPONENT_MODES.UNLIMITED);
      return;
    }
    setComponentMode(COMPONENT_MODES.LIMITED_NON_SWITCH);
  };

  const setInitialCategorizeType = () => {
    setTimeout(() => {
      if (localTransactionsCounter?.expenseTrxCnt) {
        setCategorizationType(CATEGORIZATION_TYPES.EXPENSE);
      } else if (validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME) && !!localTransactionsCounter?.incomeTrxCnt) {
        if (componentMode === COMPONENT_MODES.LIMITED_NON_SWITCH) return;
        setCategorizationType(CATEGORIZATION_TYPES.INCOME);
      } else {
        setCategorizationType(CATEGORIZATION_TYPES.EXPENSE);
      }
    }, 500);
  };

  useEffect(() => {
    fetchUncategorizedData();
  }, [queryString]);

  useEffect(() => {
    if (categorizationProgress) {
      categorizationProgress(navSwitchData);
    }
  }, [localTransactionsCounter]);

  useEffect(() => {
    checkComponentMode();
  }, [isZeroState]);

  useEffect(() => {
    if (!mutableTransactions || !mutableTransactions.length) {
      if (leftToCategorize) leftToCategorize(0);
      return;
    }
    if (leftToCategorize) leftToCategorize(mutableTransactions.length);
    if (onActiveTrxChange) onActiveTrxChange(mutableTransactions[0]);
  }, [mutableTransactions]);

  useEffect(() => {
    let qString: URLSearchParams | null = null;

    if (filterRange) {
      qString = new URLSearchParams({
        startTime: filterRange.startDate.substring(0, 10),
        endTime: filterRange.endDate.substring(0, 10),
        categorizationType,
      });
      setQueryString(qString);
      return;
    }

    const { endTime, startTime } = getFilterRangeQueryV2(filterYearValue ?? 0);

    if (!filterYearValue) {
      qString = new URLSearchParams({
        categorizationType,
      });
    } else {
      qString = new URLSearchParams({
        startTime,
        endTime,
        categorizationType,
      });
    }
    setQueryString(qString);
  }, [filterYearValue, categorizationType]);

  useEffect(() => {
    if (!toggleData) return;
    setIsMutableTransactionsSet(true);
    setLocalTransactionsCounter(toggleData);
  }, [toggleData]);

  useEffect(() => {
    if (!mutableTransactions?.length) return;
    mutableTransactions.forEach((trx: ITransaction) => {
      if (trx.suggestedTaxCategoryId && !trx?.categoryDetails?.categoryExternalId) {
        trx.categoryDetails.categoryExternalId = trx.suggestedTaxCategoryId;
      }
    });
  }, [mutableTransactions]);

  useEffect(() => {
    checkComponentMode();
    getTransactionsCategories();
  }, []);
  useEffect(() => {
    setInitialCategorizeType();
  }, [localTransactionsCounter, toggleData]);
  useEffect(() => {
    if (triggeredActionType === ACTION_TYPES.CHOOSE_CATEGORY) handleOnChooseCategoryClick();
    if ([ACTION_TYPES.LIFE, ACTION_TYPES.WORK, ACTION_TYPES.SPLIT].includes(triggeredActionType)) handleAction(triggeredActionType);
    setTriggeredActionType('');
  }, [triggeredActionType]);

  useEffect(() => {
    fetchUncategorizedData();
  }, [customerData]);

  const contextValue = useMemo(() => ({
    isReadOnlyMode,
  }), [isReadOnlyMode]);

  return isInitialLoading ? (
    <PreLoading
      containerHeader={containerHeader || ''}
      containerClassName={containerClassName}
      categorizationType={categorizationType}
      shouldShowHeader={shouldHideHeader ? false : isInitialLoading}
    />
  ) : componentMode === COMPONENT_MODES.ZERO_STATE
    ? (
      <ZeroState
        containerHeader={containerHeader || ''}
        containerClassName={containerClassName}
        shouldShowHeader={isInitialLoading ?? false}
      />
    )
    : componentMode === COMPONENT_MODES.NOT_ELIGIBLE
      ? <div className='hidden' /> : (
        <CategorizeContext.Provider value={contextValue}>
          <div className={containerClassName}>
            <div>
              {
              isCategoryPicker
              && mutableTransactions
              && (
              <CategorizationPopup
                categoryList={filteredCategories}
                handleCategoryPick={handleCategoryPick}
                transaction={mutableTransactions[0]}
                actionType={middlewareTriggerData.type ?? ''}
              />
              )
            }
            </div>
            {!shouldHideHeader && (
            <>
              <div className='w-full flex items-center px-[2px]'>
                <Heading
                  id='categorizeHeader'
                  title={containerHeader ?? isFooterShown ? `${t('CATEGORIZE_EXPENSES_COMPONENT_HEADER_PREFIX')} ${t('CATEGORIZE_EXPENSES')} ${expensesLeft ? `(${expensesLeft})` : ''}` : `${t('CATEGORIZE_EXPENSES_COMPONENT_HEADER_PREFIX')} ${t('TRANSACTIONS_PAGE_HEADER')}`}
                  className={STYLES.MAIN_TITLE}
                />
              </div>
              <div className='w-full border-b border-bw03 mb-[16px] mt-[2px]' />
            </>
            )}

            {componentMode === COMPONENT_MODES.UNLIMITED && navSwitchData && (
            <SwitchNav
              navItems={navSwitchData}
              wrapperClassName={navSwitchClassName ?? ''}
              activeTabId={categorizationType}
              onSwitch={handleSwitch}
            />
            )}
            <CategorizeMainSection
              transactionsCategories={filteredCategories}
              onCategorySelection={handleOnChooseCategoryClick}
              isLoading={isRefetching}
              handleActionFinish={handleActionFinish}
              triggerAction={triggerData}
              transactionsList={mutableTransactions}
              categorizationType={categorizationType}
              finishProcessCtaTitle={finishProcessCtaTitle}
            />

            {!isReadOnlyMode && mutableTransactions && mutableTransactions.length > 0 && (
            <CategorizeActions
              isActionActive={triggerData.isActive || isRefetching}
              emitAction={handleAction}
              actions={dynamicActions}
            />
            )}

          </div>
        </CategorizeContext.Provider>
      );
};
